import { ascending, descending } from 'd3'
import dayjs from 'dayjs'
import { makeMonthSeries } from '../../helpers/make-month-series.js'
import { valuePinnedMaker } from './value/value-pinned-maker.js'
import { computeCell } from '../../data/compute/compute-cell/index.js'
import { valueFormatter } from '../value-formatter.js'
import { cellClass } from '../cell-class.js'
import { debugOnCellClicked } from '../debug-on-cell-clicked.js'

export function makeColumnDefs (context) {
  const columnDefs = []

  // Chart of Account Code
  columnDefs.push({
    headerName: 'Code',
    valueGetter: function (cellParams) {
      return cellParams.data.chartOfAccount?.code
    },
    width: 100,
    pinned: 'left'
  })

  // Chart of Account Name
  columnDefs.push({
    headerName: 'Account',
    valueGetter: function (cellParams) {
      if (cellParams.node.rowPinned) {
        return cellParams.data.name
      }

      return cellParams.data.chartOfAccount?.name
    },
    width: 200,
    pinned: 'left'
  })

  columnDefs.push({
    headerName: 'Account Type',
    valueGetter: function (cellParams) {
      if (cellParams.node.rowPinned) {
        return null
      }

      return cellParams.data.accountType?.accountType
    },
    width: 100,
    pinned: 'left'
  })

  columnDefs.push({
    headerName: 'Origin Type',
    valueGetter: function (cellParams) {
      if (cellParams.node.rowPinned) {
        return null
      }

      const originAccountId = cellParams.data.originChartOfAccountIds?.[0]
      // console.log('originAccountId', originAccountId)
      const originAccount = context.chartOfAccountsMap[originAccountId]
      // console.log('originAccount', originAccount)

      return originAccount?.settings?.accountType
    },
    width: 100,
    pinned: 'left'
  })

  columnDefs.push({
    headerName: 'FX Applied',
    valueGetter: function (cellParams) {
      if (cellParams.node.rowPinned) {
        return null
      }

      return cellParams.data.accountType?.fxConversion
    },
    width: 100,
    pinned: 'left'
  })

  // Add the months columns
  columnDefs.push(...makeMonthsColumns(context))

  return columnDefs
}

/**
 *
 * @param {*} context
 * @returns
 */
function makeMonthsColumns (context) {
  console.log('makeMonthsColumns', context)

  const monthsArray = makeMonthSeries(context)

  monthsArray?.sort(function (m1, m2) {
    if (context.timeDirection === 'descending') {
      return descending(m1, m2)
    } else {
      return ascending(m1, m2)
    }
  })

  const targetEntity = context.browserAppAccount.data.entities.find(o => o.id === context.showingEntitiesIds[0])

  const monthColDefs = []
  monthsArray?.forEach(function (monthDate) {
    const colDef = periodColumnDef({
      monthDate,
      targetEntity,
      context
    })

    // For valueProperty:
    // valueProperty: 'valueRevaluationBalance'
    monthColDefs.push(colDef)
  })

  return monthColDefs
}

function periodColumnDef (colDefParams) {
  const showMultipleColumns = true

  const colDef = {}

  if (showMultipleColumns) {
    colDef.headerName = dayjs(colDefParams.monthDate).format('MMM YYYY')
    colDef.children = []
    colDef.columnGroupShow = 'open'
    colDef.children.push(makeDefaultValueColumnDef({ ...colDefParams }))

    // Computed from Period values
    colDef.children.push(makeDefaultValueColumnDef({ ...colDefParams, valueProperty: 'valuePeriod' }))
    colDef.children.push(makeDefaultValueColumnDef({ ...colDefParams, valueProperty: 'valuePeriodsCumul' }))

    // Computed from Balance values
    colDef.children.push(makeDefaultValueColumnDef({ ...colDefParams, valueProperty: 'valueBalanceChange' }))
    colDef.children.push(makeDefaultValueColumnDef({ ...colDefParams, valueProperty: 'valueBalance' }))

    // Computed from History Period values
    colDef.children.push(makeDefaultValueColumnDef({ ...colDefParams, valueProperty: 'valueHistoryPeriod' }))
    colDef.children.push(makeDefaultValueColumnDef({ ...colDefParams, valueProperty: 'valueHistoryPeriodsCumul' }))

    // Revaluation
    colDef.children.push(makeDefaultValueColumnDef({ ...colDefParams, valueProperty: 'valueRevaluationChange' }))
    colDef.children.push(makeDefaultValueColumnDef({ ...colDefParams, valueProperty: 'valueRevaluationBalance' }))
  } else {
    const defaultValueColumnDef = makeDefaultValueColumnDef(colDefParams)
    console.log('defaultValueColumnDef', defaultValueColumnDef)
    console.log('...defaultValueColumnDef', { ...defaultValueColumnDef })
    Object.assign(colDef, { ...defaultValueColumnDef })
    console.log('colDef', colDef)
    colDef.headerName = dayjs(colDefParams.monthDate).format('MMM YYYY')
  }
  return colDef
}

function makeDefaultValueColumnDef (colDefParams) {
  const { monthDate, targetEntity, context } = colDefParams
  const colDef = {
    // headerTooltip: colDefParams.context.valueProperty,
    valueGetter: function (cellParams) {
      if (cellParams.node.rowPinned) {
        const cellValue = valuePinnedMaker(cellParams, context)
        // console.log('valuePinnedMaker -> cellValue', cellValue)
        return cellValue
      }

      const cellValue = computeCell(cellParams)
      return cellValue
    },
    valueFormatter,
    cellClass,
    context: {
      // month: oneMonth,

      // For computing:
      monthDate,
      accountingOrganisation: targetEntity,
      isValue: true
    },
    type: 'numericColumn',
    width: 90,
    onCellClicked: debugOnCellClicked
  }

  if (colDefParams.valueProperty) {
    colDef.headerName = colDefParams.valueProperty
    colDef.context.valueProperty = colDefParams.valueProperty
  } else {
    colDef.headerName = 'Value'
  }

  return colDef
}
